// PDF raporu — yatay (landscape) A4, iki sayfa, siyah-beyaz.
// Türkçe karakterlerin (ş/ğ/ı/İ) hatasız çıkması için metin bölümleri
// tarayıcı tarafından çizilip (html-to-image) rasterize edilir; @react-pdf'in
// yerleşik fontları bu karakterleri içermediğinden tercih edilmedi.
// 1. sayfa = üst blok (başlık + künye + süre oluşumu + lejant), sayfaya ortalanır.
// 2. sayfa = timeline görüntüsü; en-boru korunarak tam sayfaya sığdırılır.

import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import { buildRaporVerisi, raporDosyaAdi, RAPOR_FERAGAT } from './reportData';
import { captureTimeline, nodeToImage, fontlariBekle } from './timelineCapture';
import './raporTheme.css';

// PDF için JPEG kullanılır: PNG'yi jsPDF neredeyse sıkıştırmasız gömdüğünden
// dosya onlarca MB'a çıkıyordu. B&W rapor için JPEG kalite kaybı görünmez.
const PDF_IMAGE = { format: 'jpeg', quality: 0.92 };

function ustBlokHTML(rd) {
  const sec = rd.secimler || {};
  const kunye = rd.kunye
    .map((i) => `<div class="cell"><div class="k">${i.etiket}</div><div class="v">${i.deger}</div></div>`)
    .join('');
  const sure = rd.sureOlusumu
    .map(
      (i) =>
        `<div class="cell"><div class="k">${i.etiket}</div><div class="v">${i.deger}</div>${
          i.alt ? `<div class="a">${i.alt}</div>` : ''
        }</div>`
    )
    .join('');

  const sureBlok = sec.sureOlusumu
    ? `<div class="rpr-sec-title">Sürenin oluşumu</div><div class="rpr-grid sure">${sure}</div>`
    : '';

  const asamaSatirlari = (rd.asamalar || [])
    .map(
      (a) =>
        `<tr class="${a.accent ? 'acc' : ''}"><td>${a.from} <span class="arr">→</span> <b>${a.to}</b>${
          a.ref ? ` <span class="ref">${a.ref}</span>` : ''
        }</td><td>${a.sure}</td><td>${a.gun} gün</td></tr>`
    )
    .join('');
  const asamaBlok =
    sec.asamalar && (rd.asamalar || []).length
      ? `<div class="rpr-sec-title">Aşama aşama geçen süre</div>
         <table class="rpr-asama"><thead><tr><th>Aşama</th><th>Geçen süre</th><th>Toplam gün</th></tr></thead><tbody>${asamaSatirlari}</tbody></table>`
      : '';

  return `
    <div class="rpr-head">
      <div>
        <h1 class="rpr-title">Dava Zamanaşımı Raporu</h1>
        <div class="rpr-sub">TCK 66–67 uyarınca dava zamanaşımı değerlendirmesi</div>
      </div>
      <div class="rpr-pill ${rd.doldu ? 'dolmus' : ''}">${rd.durumMetni}${
        rd.durumDetay ? ` <span style="font-size:0.72em;font-weight:400;opacity:0.7;">(${rd.durumDetay})</span>` : ''
      }</div>
    </div>
    <div class="rpr-result">
      <span class="lbl">Geçerli zamanaşımı (${rd.gecerliTur})</span>
      <span class="val">${rd.effectiveUzun}</span>
    </div>
    <div class="rpr-note">${rd.gerekce}</div>
    <div class="rpr-grid kunye">${kunye}</div>
    ${sureBlok}
    ${asamaBlok}
    <div class="rpr-legend">
      <span><b>Olağan</b> koyu düz şerit</span>
      <span><b>Olağanüstü (67/4)</b> gri kesik şerit</span>
      <span><b>Durma</b> taralı bant</span>
      <span><b>Kesme</b> süreyi sıfırlar</span>
      <span><b>Bugün</b> kesik dikey çizgi</span>
      <span><b>Sonuç</b> dolmuş = taralı · kalan = boş</span>
    </div>`;
}

async function captureUstBlok(rd) {
  const host = document.createElement('div');
  host.className = 'zsh-rapor';
  host.setAttribute('style', 'position:fixed;left:-100000px;top:0;z-index:-1;');
  host.innerHTML = ustBlokHTML(rd);
  document.body.appendChild(host);
  try {
    await fontlariBekle();
    return await nodeToImage(host, PDF_IMAGE);
  } finally {
    host.remove();
  }
}

// Hukuki feragati küçük bir görsel olarak yakalar (Türkçe karakterler için; jsPDF metni
// ş/ğ/ı'yı bozardı). Her sayfanın altına basılır.
async function captureFeragat() {
  const host = document.createElement('div');
  host.setAttribute(
    'style',
    'position:fixed;left:-100000px;top:0;width:1040px;background:#ffffff;padding:4px 12px;' +
      'font-family:"Hanken Grotesk",system-ui,sans-serif;font-size:12px;line-height:1.45;' +
      'font-style:italic;color:#6b7280;text-align:center;z-index:-1;'
  );
  host.textContent = RAPOR_FERAGAT;
  document.body.appendChild(host);
  try {
    await fontlariBekle();
    return await nodeToImage(host, PDF_IMAGE);
  } finally {
    host.remove();
  }
}

export async function indirPdfRapor(sonuc, secimler) {
  const rd = buildRaporVerisi(sonuc, secimler);

  // Bölümleri paralel yakala (PDF için JPEG → küçük dosya).
  const [ust, tl, ftr] = await Promise.all([
    captureUstBlok(rd),
    captureTimeline(sonuc.timelineModel, sonuc, PDF_IMAGE),
    captureFeragat(),
  ]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 26; // kenar boşluğu (pt) ~0.9cm
  const contentW = pageW - 2 * M;
  const fmt = (img) => (img.format || 'png').toUpperCase();

  // Feragat: tam içerik genişliğinde, en-boruna göre yükseklikte; en altta. (Aspect korunur.)
  const FERAGAT_GAP = 6;
  const feragatH = contentW / ftr.aspect;
  const contentH = pageH - 2 * M - feragatH - FERAGAT_GAP; // ana görsel alanı feragatin üstünde

  // Bir görüntüyü içerik alanına (en-boru koruyarak) sığdırıp ortalar, çizer.
  const yerlestir = (img) => {
    let w = contentW;
    let h = w / img.aspect;
    if (h > contentH) {
      h = contentH;
      w = h * img.aspect;
    }
    const x = M + (contentW - w) / 2;
    const y = M + (contentH - h) / 2;
    doc.addImage(img.dataUrl, fmt(img), x, y, w, h, undefined, 'FAST');
  };

  // Her sayfanın altına feragat görselini basar.
  const cizFeragat = () => {
    doc.addImage(ftr.dataUrl, fmt(ftr), M, pageH - M - feragatH, contentW, feragatH, undefined, 'FAST');
  };

  // 1. sayfa: özet bilgiler (üst blok) + feragat.
  yerlestir(ust);
  cizFeragat();

  // 2. sayfa: timeline grafiği + feragat.
  doc.addPage('a4', 'landscape');
  yerlestir(tl);
  cizFeragat();

  const blob = doc.output('blob');
  saveAs(blob, raporDosyaAdi(sonuc, 'pdf'));
}
